import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { 
  Users,
  ArrowRight,
  Zap,
  Smartphone,
  Globe,
  Shield
} from 'lucide-react'

export const SpecializedSolutions: React.FC = () => {
  const { t } = useTranslation('homepage')
  return (
    <section className="py-24 bg-gradient-to-br from-orange-50 via-white to-red-50">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-6"
          >
            <Zap className="w-4 h-4" />
            {t('specializedSolutions.badge')}
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('specializedSolutions.title')}
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('specializedSolutions.subtitle')}
          </motion.p>
        </div>

        {/* WorkForce Platform Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-shadow duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Content */}
              <div className="p-8 lg:p-12">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-orange-50 mb-6">
                  <Users className="w-6 h-6 text-orange-600" />
                  <span className="font-semibold text-orange-600">{t('specializedSolutions.workforce.badge')}</span>
                </div>
                
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {t('specializedSolutions.workforce.title')}
                </h3>
                
                <p className="text-lg text-gray-600 mb-8">
                  {t('specializedSolutions.workforce.description')}
                </p>

                {/* Key Features */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">{t('specializedSolutions.workforce.features.0')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">{t('specializedSolutions.workforce.features.1')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">{t('specializedSolutions.workforce.features.2')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700">{t('specializedSolutions.workforce.features.3')}</span>
                  </div>
                </div>

                {/* Tech Badges */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {['React 19', 'Spring Boot', 'PostgreSQL', 'WebSocket', 'PWA'].map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link to="/workforce">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-flex items-center gap-2"
                  >
                    {t('specializedSolutions.workforce.cta')}
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
              </div>

              {/* Right Content - Visual */}
              <div className="bg-gradient-to-br from-orange-100 to-red-100 p-8 lg:p-12 flex items-center justify-center">
                <div className="relative">
                  {/* Mock Dashboard */}
                  <div className="bg-white rounded-xl shadow-lg p-6 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center">
                        <Users className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-semibold text-gray-900">WorkForce Dashboard</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-medium">{t('specializedSolutions.workforce.dashboard.onlineEmployees')}</span>
                        </div>
                        <span className="text-sm text-gray-600">12/15</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-medium">{t('specializedSolutions.workforce.dashboard.activeTasks')}</span>
                        </div>
                        <span className="text-sm text-gray-600">28</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <span className="text-sm font-medium">{t('specializedSolutions.workforce.dashboard.productivity')}</span>
                        </div>
                        <span className="text-sm text-gray-600">94%</span>
                      </div>
                    </div>
                  </div>

                  {/* Floating Mobile */}
                  <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 transform -rotate-12 hover:rotate-0 transition-transform duration-500">
                    <div className="w-16 h-24 bg-gradient-to-b from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('specializedSolutions.workforce.dashboard.mobilePwa')}</div>
                  </div>

                  {/* Floating Icons */}
                  <div className="absolute -bottom-2 -left-2 flex space-x-2">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      <Globe className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-500 text-sm max-w-2xl mx-auto">
            {t('specializedSolutions.bottomNote')}
          </p>
        </motion.div>
      </div>
    </section>
  )
}